import React, { useCallback, useState, useEffect, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./components/assets/css/LiveChat.css";
import "./custom.css";

// Eager-load critical path components
import Home from './components/pages/mobile/Home';
import Login from './components/pages/mobile/Login';
import SignUp from './components/pages/mobile/SignUp';
import Splash from './splash/Splash';
import APIS from './components/pages/mobile/common/apis';
import GoToTop from './components/common/GoTop';
import { Modal } from "react-bootstrap"; 
import Maintenance from './components/pages/mobile/common/Maintenance';
import StoreCode from './StoreCodeComponents';
import { fetchLogo, fetchGeneral, fetchBalance as apiFetchBalance } from './api';

// Lazy-load non-critical routes
const MAccount = lazy(() => import('./components/pages/mobile/MAccount'));
const Profile = lazy(() => import('./components/pages/mobile/Profile'));
const MDeposit = lazy(() => import('./components/pages/mobile/MDeposit'));
const MWithdrawal = lazy(() => import('./components/pages/mobile/MWithdrawal'));
const BettingRecord = lazy(() => import('./components/pages/mobile/BettingRecord'));
const TransactionRecord = lazy(() => import('./components/pages/mobile/TransactionRecord'));
const TurnoverRecord = lazy(() => import('./components/pages/mobile/TurnoverRecord'));
const RefrrelProgram = lazy(() => import('./components/pages/mobile/RefrrelProgram'));
const ResetPassword = lazy(() => import('./components/pages/mobile/ResetPassword'));
const MobileInbox = lazy(() => import('./components/pages/mobile/MobileInbox'));
const VIPConvert = lazy(() => import('./components/pages/mobile/VIPConvert'));
const Games = lazy(() => import('./components/pages/mobile/Games'));
const GameRun = lazy(() => import('./components/pages/mobile/GameRun'));
const MobilePromotion = lazy(() => import('./components/pages/mobile/MobilePromotion'));
const ChatHome = lazy(() => import('./components/Chat/ChatHome'));
const ChatHistory = lazy(() => import('./components/Chat/ChatHistory'));
const ChatBox = lazy(() => import('./components/Chat/ChatBox'));
const EXHome = lazy(() => import('./components/exchanges/EXHome'));
const EXHomeVelki = lazy(() => import('./components/exchanges/EXHomeVelki'));
const MatchOdds = lazy(() => import('./components/exchanges/MatchOdds'));
const ExAccount = lazy(() => import('./components/exchanges/ExAccount'));
const InPlay = lazy(() => import('./components/exchanges/InPlay'));
const PaymentTimer = lazy(() => import('./components/pages/mobile/common/PaymentTimer'));
const ForgotPassword = lazy(() => import('./components/pages/mobile/ForgotPassword'));
const MakePayment = lazy(() => import('./components/pages/mobile/common/MakePayment'));
const FreeSpinPage = lazy(() => import('./free-spin/FreeSpinPage'));
const NineWiket = lazy(() => import('./components/pages/mobile/NineWiket'));
const Registion = lazy(() => import('./components/pages/desktop/Registion'));

// Simple text sanitizer – strips HTML tags to prevent XSS
const sanitizeText = (str) => {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

// Loading fallback for lazy routes
const LazyFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <div className="three-dots-loading"><span></span><span></span><span></span></div>
  </div>
);

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
  const [showContent, setShowContent] = useState(false);
  const location = useLocation();

  // ── /#/9wkt exit handler ──────────────────────────────────────────────────
  useEffect(() => {
    if (window.location.href.includes('/#/9wkt')) {
      const token = localStorage.getItem('token');
      fetch(`${process.env.REACT_APP_API_URL}/api/9wkt/exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }).finally(() => {
        const cleanUrl = window.location.href.split('/#/9wkt')[0];
        window.location.replace(cleanUrl);
      });
    }
  }, []);

  const [globalData, setGlobalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalType, setModalType] = useState('error');
  const [refreshPvtData, setRefreshPvtData] = useState(false);
  const [pwaIconUrl, setPwaIconUrl] = useState(null);
  const [pwaSiteName, setPwaSiteName] = useState(null);

  // ── Alert modal (sanitized – no XSS) ──────────────────────────────────────
  const showAlert = useCallback((message = false, type = 'error') => {
    if (!message) {
      setShowModal(false);
      setModalMessage('');
      return;
    }
    setShowModal(true);
    setModalType(type || 'error');
    // Sanitize any HTML from API error messages before rendering
    setModalMessage(sanitizeText(String(message)));
  }, []);

  // ── Favicon helper ────────────────────────────────────────────────────────
  const setFavicon = useCallback((url) => {
    if (!url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = url;
  }, []);

  // ── PWA manifest helper ───────────────────────────────────────────────────
  const updateManifest = useCallback((siteName, iconUrl) => {
    const manifestData = {
      short_name: siteName || document.title,
      name: siteName || document.title,
      start_url: '/',
      display: 'standalone',
      theme_color: '#000000',
      background_color: '#ffffff',
      icons: iconUrl
        ? [{ src: iconUrl, sizes: '192x192', type: 'image/png' }, { src: iconUrl, sizes: '512x512', type: 'image/png' }]
        : [],
    };
    const blob = new Blob([JSON.stringify(manifestData)], { type: 'application/json' });
    const manifestUrl = URL.createObjectURL(blob);
    let manifestLink = document.querySelector("link[rel='manifest']");
    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }
    manifestLink.href = manifestUrl;
    return () => URL.revokeObjectURL(manifestUrl);
  }, []);

  // ── Combined site data fetch (single call, replaces duplicate logo+general fetches) ──
  useEffect(() => {
    let cancelled = false;
    const loadSiteData = async () => {
      try {
        const origin = window.location.hostname.replace(/^www\./, '');
        const [logoData, generalData] = await Promise.all([
          fetchLogo(),
          fetchGeneral(),
        ]);

        if (cancelled) return;

        // Process logo/favicon data
        const item = Array.isArray(logoData) ? logoData[0] : logoData;
        if (item?.favicon) {
          const faviconUrl = item.favicon.startsWith('http')
            ? item.favicon
            : `${process.env.REACT_APP_API_URL}/storage/${item.favicon}`;
          setFavicon(faviconUrl);
          const pwaIcon = item?.logo
            ? (item.logo.startsWith('http') ? item.logo : `${process.env.REACT_APP_API_URL}/storage/${item.logo}`)
            : faviconUrl;
          setPwaIconUrl(pwaIcon);
        }

        // Process general site data
        const genItem = Array.isArray(generalData) ? generalData[0] : generalData;
        if (genItem) {
          document.title = genItem.site_title || document.title;
          setPwaSiteName(genItem.site_title || null);
          localStorage.setItem('whatsapp', genItem.whatsapp || '');
          localStorage.setItem('liveChat', genItem.live_chat || '');
          localStorage.setItem('intercom', genItem.intercom || '');
          if (genItem.tawk_id) localStorage.setItem('tawkId', genItem.tawk_id);
        }
      } catch (err) {
        // Silently handle – data already fetched by APIS component
      }
    };
    loadSiteData();
    return () => { cancelled = true; };
  }, [setFavicon]);

  // ── Update PWA manifest when data arrives ─────────────────────────────────
  useEffect(() => {
    if (!pwaSiteName && !pwaIconUrl) return;
    return updateManifest(pwaSiteName, pwaIconUrl);
  }, [pwaSiteName, pwaIconUrl, updateManifest]);

  // ── Balance fetch (optimized: 30s interval, visibility-aware) ─────────────
  const fetchBalanceFn = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const data = await apiFetchBalance();
      if (data?.balance !== undefined) {
        localStorage.setItem('balance', data.balance);
        const el = document.querySelector('.mainBalance');
        if (el) el.textContent = data.balance; // textContent, not innerHTML
      }
    } catch (err) {
      if (err?.status === 401) handleLogout();
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    window.location.href = '/';
  }, []);

  // Balance polling at 30s (was 10s), skips when tab hidden
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchBalanceFn();
    const interval = setInterval(() => {
      if (document.hidden) return; // skip when tab not visible
      fetchBalanceFn();
    }, 30000);
    // Also refresh on visibility change
    const onVisible = () => { if (!document.hidden) fetchBalanceFn(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isLoggedIn, fetchBalanceFn]);

  // ── Private route guard ────────────────────────────────────────────────────
  const PrivateRoute = useCallback(({ element }) => {
    return isLoggedIn ? element : <Navigate to="/login" />;
  }, [isLoggedIn]);

  // ── CSS loading ───────────────────────────────────────────────────────────
  const [cssLoading, setCssLoading] = useState(false);
  useEffect(() => {
    let isMounted = true;
    let extraCssLink;

    const loadExtraCss = () => {
      return new Promise((resolve, reject) => {
        if (location.pathname === "/make-payment") {
          extraCssLink = document.createElement("link");
          extraCssLink.rel = "stylesheet";
          extraCssLink.href = "./extra.css";
          extraCssLink.id = "extra-css";
          extraCssLink.onload = resolve;
          extraCssLink.onerror = reject;
          document.head.appendChild(extraCssLink);
        } else {
          resolve();
        }
      });
    };

    const loadOtherCss = async () => {
      await import("./components/assets/css/flipclock.css");
      await import("./components/assets/css/Mobile.css");
    };

    const loadAllCss = async () => {
      setCssLoading(true);
      try {
        await Promise.all([loadExtraCss(), loadOtherCss()]);
      } catch (err) {
        // CSS load failure is non-critical
      } finally {
        if (isMounted) setCssLoading(false);
      }
    };

    loadAllCss();

    return () => {
      isMounted = false;
      const existingLink = document.getElementById("extra-css");
      if (existingLink) document.head.removeChild(existingLink);
    };
  }, [location.pathname]);

  // ── GTM ────────────────────────────────────────────────────────────────────
  const [gtmId, setGtmId] = useState(null);
  useEffect(() => {
    const id = globalData?.general?.google_tag;
    if (!id) return;
    setGtmId(id);
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
    }
    const oldScript = document.getElementById("gtm-script");
    if (oldScript) oldScript.remove();

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(script);

    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

    return () => {
      const s = document.getElementById("gtm-script");
      if (s) s.remove();
    };
  }, [globalData]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="App">
      {gtmId && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0" width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="gtm"
          />
        </noscript>
      )}
      <GoToTop />
      <APIS setGlobalData={setGlobalData} refreshPvtData={refreshPvtData} />

      <Splash onSplashComplete={() => {
        setShowContent(true);
        setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
      }} />

      {cssLoading && <Splash />}

      {showContent && (
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/" element={
              globalData?.general?.site_status === 0 ? <Maintenance /> : <Home globalData={globalData} isLoggedIn={isLoggedIn} />
            } />
            <Route path="/signup" element={<SignUp slidersMain={globalData.sliders?.main || []} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/games" element={<Games />} />
            <Route path="/forgot-password" element={<ForgotPassword showAlert={showAlert} />} />
            <Route path="/9wiket" element={<NineWiket />} />
            <Route path="/account" element={<PrivateRoute element={<MAccount fetchBalance={fetchBalanceFn} userData={globalData.userData} general={globalData.general || {}} sociallink={globalData.socialLinks || {}} />} />} />
            <Route path="/profile" element={<PrivateRoute element={<Profile />} />} />
            <Route path="/deposit" element={<PrivateRoute element={<MDeposit usdtRate={globalData?.general?.usdt_rate} hasEWallet={globalData.hasEWallet} userData={globalData.userData || []} transactionData={globalData.transactions || []} items={globalData.paymentMethods || []} promotions={globalData.promotions?.activePromotion || []} />} />} />
            <Route path="/withdrawal" element={<PrivateRoute element={<MWithdrawal general={globalData.general} hasEWallet={globalData.hasEWallet} userData={globalData.userData || []} transactionData={globalData.transactions || []} items={globalData.paymentMethods || []} />} />} />
            <Route path="/betting-record" element={<PrivateRoute element={<BettingRecord />} />} />
            <Route path="/transaction-record" element={<PrivateRoute element={<TransactionRecord setRefreshPvtData={setRefreshPvtData} transactionData={globalData.transactions || []} />} />} />
            <Route path="/turnover-record" element={<PrivateRoute element={<TurnoverRecord turnovers={globalData.turnovers || []} />} />} />
            <Route path="/refrrel-program" element={<PrivateRoute element={<RefrrelProgram showAlert={showAlert} userData={globalData.userData} />} />} />
            <Route path="/reset-password" element={<PrivateRoute element={<ResetPassword showAlert={showAlert} />} />} />
            <Route path="/inbox" element={<PrivateRoute element={<MobileInbox />} />} />
            <Route path="/vip" element={<PrivateRoute element={<VIPConvert />} />} />
            <Route path="/gamerun" element={<PrivateRoute element={<GameRun />} />} />
            <Route path="/promotions" element={<PrivateRoute element={<MobilePromotion promotions={globalData.promotions?.allPromotions || []} />} />} />
            <Route path="/chat" element={<PrivateRoute element={<ChatHome />} />} />
            <Route path="/chat-list" element={<PrivateRoute element={<ChatHistory />} />} />
            <Route path="/chat-box/:chatId" element={<PrivateRoute element={<ChatBox />} />} />
            <Route path="/odds-view" element={<PrivateRoute element={<MatchOdds />} />} />
            <Route path="/exchange" element={<PrivateRoute element={<EXHome />} />} />
            <Route path="/velki" element={<PrivateRoute element={<EXHomeVelki />} />} />
            <Route path="/exchange-account" element={<PrivateRoute element={<ExAccount />} />} />
            <Route path="/exchange-inplay" element={<PrivateRoute element={<InPlay />} />} />
            <Route path="/payment-request" element={<PrivateRoute element={<PaymentTimer />} />} />
            <Route path="/referral_code" element={<StoreCode codeType="referral_code" />} />
            <Route path="/affiliate_code" element={<StoreCode codeType="affiliate_code" />} />
            <Route path="/agent_code" element={<StoreCode codeType="agent_code" />} />
            <Route path="/make-payment" element={<MakePayment hasEWallet={globalData.hasEWallet} userData={globalData.userData || []} />} />
            <Route path="/free-spin" element={<FreeSpinPage />} />
            <Route path="/register" element={<Registion />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <div>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title>{modalType === 'success' ? 'Success' : modalType === 'info' ? 'Notice' : 'Error'}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {/* SAFE: modalMessage is sanitized via textContent in showAlert */}
                <div>{modalMessage}</div>
              </Modal.Body>
            </Modal>
          </div>
        </Suspense>
      )}
    </div>
  );
}

export default App;
